<template>
	<div class="chart-container" :style="{'height': containerHeight}">
		<canvas ref="chart"></canvas>
	</div>
</template>

<script>
	import { Chart, registerables } from 'chart.js';
	Chart.register(...registerables);

	export default ({
		props: {
			data: {
				type: Array,
				default: () => []
			},
			height: {
				type: Number,
				default: 200
			},
			options: {
				type: Object,
				default: () => ({})
			}
		},
		data(){
			return {
				chart: null,
			} ;
		},
		computed: {
			containerHeight() {
				return this.height ? (this.height + 'px') : '100%';
			}
		},
		watch: {
			// 监听数据变化，更新图表
			data: {
				handler(newData) {
					if (this.chart) {
						this.updateChart(newData);
					}
				},
				deep: true
			}
		},
		mounted () { 
			this.initChart();
		},
		// Right before the component is destroyed,
		// also destroy the chart.
		beforeDestroy: function () {
			if (this.chart) {
				this.chart.destroy();
			}
		},
		methods: {
			// 初始化图表
			initChart() {
				const ctx = this.$refs.chart.getContext("2d");
				const chartData = this.transformData(this.data);

				this.chart = new Chart(ctx, {
					type: "line",
					data: chartData,
					options: this.getChartOptions()
				});
			},

			// 转换数据格式为 Chart.js 需要的格式
			transformData(rawData) {
				if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
					return { labels: [], datasets: [] };
				}

				// 检查数据格式：[{ date: '2026-01-01', value: 10, label: 'xxx' }]
				const labels = rawData.map(item => item.date || item.label || '');
				const values = rawData.map(item => item.value || item.y || 0);
				
				// 为每个数据点创建样式数组，最后一个点（最新数据）高亮
				const lastIndex = values.length - 1;
				const pointRadius = values.map((_, index) => index === lastIndex ? 6 : 3);
				const pointBackgroundColor = values.map((_, index) => 
					index === lastIndex ? '#ff4d4f' : '#1890ff'
				);
				const pointBorderColor = values.map((_, index) => 
					index === lastIndex ? '#ff7875' : '#40a9ff'
				);
				const pointBorderWidth = values.map((_, index) => 
					index === lastIndex ? 3 : 2
				);

				return {
					labels: labels,
					datasets: [{
						data: values,
						borderColor: '#1890ff',
						backgroundColor: 'rgba(24, 144, 255, 0.1)',
						fill: true,
						tension: 0.4,
						pointRadius: pointRadius,
						pointHoverRadius: 6,
						pointBackgroundColor: pointBackgroundColor,
						pointBorderColor: pointBorderColor,
						pointBorderWidth: pointBorderWidth
					}]
				};
			},

			// 更新图表数据
			updateChart(newData) {
				const chartData = this.transformData(newData);
				this.chart.data = chartData;
				this.chart.update();
			},

			// 获取图表配置
			getChartOptions() {
				const customOptions = this.options || {};
				return {
					layout: {
						padding: {
							top: 10,
							right: 10,
							left: 10,
							bottom: 5,
						},
					},
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: {
							display: false,
						},
						title: customOptions.title ? {
							display: true,
							text: customOptions.title
						} : false,
					},
					scales: {
						y: {
							grid: {
								display: true,
								color: "rgba(0, 0, 0, .1)",
							},
							ticks: {
								suggestedMin: 0,
								display: true,
								color: "#666",
								font: {
									size: 12,
								},
							},
							title: customOptions.yAxisLabel ? {
								display: true,
								text: customOptions.yAxisLabel
							} : undefined
						},
						x: {
							grid: {
								display: false,
							},
							ticks: {
								display: true,
								color: "#666",
								font: {
									size: 11,
								},
								maxRotation: 45,
								minRotation: 0,
							},
						},
					},
				};
			}
		}
	})

</script>

<style lang="scss" scoped>
.chart-container {
	width: 100%;
	height: 100%;
	position: relative;
	min-height: 150px;
}
</style>
